import { JWMButton } from "@components/JWMButton/JWMButton";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Color } from "@jmc/solid-design-system/src/components/atoms/Hero/enum/Color.enum";
import {
    ContentAlignment,
    ContentVerticalAlignment,
    Hero,
} from "@jmc/solid-design-system/src/components/atoms/Hero/Hero";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { getVisitedLinkColor } from "@utils/colorsUtil";
import classnames from "classnames";
import { ServiceLink } from "components/JWMButton/JWMButton";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";

import style from "./style.module.scss";

interface Props {
    id: string;
    title?: string;
    description?: string;
    image?: IGatsbyImageData;
    footer?: {
        buttonProps: React.ComponentProps<typeof Button>;
        link: { external?: boolean; url?: string; url_prefix?: string; title: string; anchor_id: string };
        text: string;
        serviceLink: ServiceLink;
    }[];
    fullWidth?: boolean;
    showInHeader?: boolean;
    contentAlignment?: ContentAlignment;
    contentVerticalAlignment?: ContentVerticalAlignment;
    hasOverlay?: boolean;
    height?: string;
    placement?: string;
    subtitleColor?: Color;
    linkColor?: Color;
    imagePosition?: ImagePosition;
    carouselHeight?: string;
    titleComponent?: React.ElementType;
    loading?: "eager" | "lazy";
    siteSpecialty?: {
        type: string;
        title: string;
    };
    showGlossary?: boolean;
    isCarouselSlider?: boolean;
    inFullWidthSection?: boolean;
    overflow?: boolean;
    children?: JSX.Element;
}

export const JWMHero = (props: Props): JSX.Element => {
    const [visitedLinkColor, setVisitedLinkColor] = useState(undefined);

    useEffect(() => {
        setVisitedLinkColor(getVisitedLinkColor(props?.linkColor));
    }, []);

    const heroTitle = props?.title ? (
        <JWMHtml color={"inherit"} showGlossary={false}>
            {props.title}
        </JWMHtml>
    ) : (
        ""
    );

    const isButtonExist = props?.footer?.filter((button) => {
        if ((button?.link?.title && button?.link?.url) || button?.serviceLink?.servicePage) {
            return button;
        }
    });

    const heroFooter = props?.footer?.map((button, index) => {
        if ((button?.link?.title && button?.link?.url) || button?.serviceLink?.servicePage) {
            return (
                <JWMButton
                    key={index}
                    buttonProps={button?.buttonProps}
                    text={button?.text}
                    anchor_id={button?.link?.anchor_id}
                    url={button?.link?.url}
                    url_prefix={button?.link?.url_prefix}
                    fullWidth={false}
                    external={button?.link?.external}
                    placement={props?.placement}
                    serviceLink={button?.serviceLink}
                />
            );
        }
    });

    const heroContent = props.description ? (
        <JWMHtml
            color={"inherit"}
            placement={props?.placement}
            siteSpecialty={props?.siteSpecialty}
            showGlossary={props?.showGlossary === undefined ? true : props?.showGlossary}
        >
            {props.description}
        </JWMHtml>
    ) : null;

    // This is a temporary "quickfix" for AHAY-2441.  The 'overlay' prop was
    // added to the Hero component because JMC needs to distinguish between
    // three values for overlay now:  none, dark or light.  Prior to this
    // JMC requirement the hasOverlay boolean prop was sufficient. We can "fix"
    // the issue by setting overlay to dark if hasOverlay is true, but we still
    // need to remove the 'hasOverlay' prop from the Hero component and adjust
    // all invocations of <Hero> accordingly.
    const overlay = props?.hasOverlay ? "dark" : "none";
    const hero = (
        <Hero
            {...{ ...props, visitedLinkColor }}
            overlay={overlay}
            title={heroTitle}
            footer={heroFooter}
            image={null}
            wideText={true}
            titleComponent={props?.titleComponent}
            titleVariant={props.showInHeader ? "h2" : "h3"}
        >
            {heroContent || props?.children}
        </Hero>
    );

    const loading = props?.loading ? props.loading : "lazy";

    return (
        <div
            className={classnames(
                style.jwm_hero,
                style[`jwm_hero__carouselHeight_${props.carouselHeight}`],
                props.overflow && style.overflow,
                props.fullWidth && style.jwm_hero__fullWidth,
                props.showInHeader && style.jwm_hero__header,
                props.inFullWidthSection && style.jwm_hero__inFullWidthSection,
                style.background,
                style[`background__align_${props.contentVerticalAlignment || "center"}`],
                props.isCarouselSlider && isButtonExist?.length ? style.carouselControls : "",
            )}
        >
            {hero}
            {props?.image && (
                <GatsbyImage
                    image={props?.image}
                    alt={props?.title || ""}
                    loading={loading}
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        insetInlineStart: 0,
                    }}
                    imgStyle={{ objectPosition: props?.imagePosition?.replace("-", " ") }}
                />
            )}
        </div>
    );
};
export default JWMHero;
